import { useState } from "react";
import { Group, Button, Badge, Drawer, Stack, Box } from "@mantine/core";
import { IconFilter } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

/**
 * Presents filter controls responsively so they stop eating the whole first screen on a phone.
 *
 *   Desktop (sm+): the optional search box + all controls sit inline, exactly as before.
 *   Mobile (<sm):  only the search box stays visible; every other control collapses behind one
 *                  "Filter" button carrying an active-count badge, which opens a bottom Drawer
 *                  holding the same controls.
 *
 * The PAGE keeps owning all filter state — this primitive owns only the inline↔drawer presentation
 * and the active-count. `children` are the control elements (Selects, chips, toggles, …); they are
 * the same controlled elements in both places, so a change in the drawer updates the page state and
 * both instances re-render consistently. `trailing` is an always-visible element pinned to the right
 * of the bar on both desktop and mobile (e.g. a sort control), so it stays aligned with the filters.
 */
export default function FilterBar({ search, activeCount = 0, drawerTitle, trailing, children }) {
  const { t } = useTranslation("Veft");
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Group gap="sm" wrap="nowrap" align="center">
        {search && <Box style={{ flex: 1, minWidth: 0 }}>{search}</Box>}

        {/* Desktop: controls inline (wrap as needed). */}
        <Group gap="xs" wrap="wrap" align="center" visibleFrom="sm" style={search ? undefined : { flex: 1 }}>
          {children}
        </Group>

        {/* Mobile: a single button opens the drawer. */}
        <Button
          hiddenFrom="sm"
          variant={activeCount > 0 ? "light" : "default"}
          leftSection={<IconFilter size={16} />}
          rightSection={activeCount > 0 ? <Badge size="sm" circle variant="filled">{activeCount}</Badge> : null}
          onClick={() => setOpened(true)}
          style={{ flexShrink: 0 }}
        >
          {t("filter")}
        </Button>

        {trailing && <Box style={{ marginLeft: "auto", flexShrink: 0 }}>{trailing}</Box>}
      </Group>

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="bottom"
        // Content-hugging bottom sheet. Mantine v9 has no size="auto" for Drawer (it resolves to an
        // undefined --drawer-size-auto var, which makes a bottom drawer fill the whole viewport), so
        // set the height explicitly: auto to hug the controls, capped so a long filter set scrolls.
        styles={{ content: { height: "auto", maxHeight: "85vh" } }}
        title={drawerTitle ?? t("filter")}
        hiddenFrom="sm"
      >
        <Stack gap="md" pb="md">{children}</Stack>
      </Drawer>
    </>
  );
}
